import { useSyncExternalStore } from "react";

/**
 * Visitor-selectable accent colour (Settings panel). The default (lime in dark
 * mode, lavender in light mode) is baked into the tokens in index.css; an
 * explicit pick is stored in localStorage and injected as a <style> override of
 * the accent tokens on every page, in both themes.
 */

export type AccentId = "lime" | "mint" | "cyan" | "lavender" | "amber" | "blue";

export interface AccentOption {
    /** "default" is the unpicked per-theme mix, not one of ACCENT_OPTIONS. */
    id: AccentId | "default";
    /** Dark-mode accent (the candidate as proposed). */
    dark: string;
    /** Darker shade that holds contrast on the light background. */
    light: string;
}

export const ACCENT_OPTIONS: ReadonlyArray<AccentOption & { id: AccentId }> = [
    { id: "lime", dark: "#A3E635", light: "#4D7C0F" },
    { id: "mint", dark: "#63D6A5", light: "#0E7F57" },
    { id: "cyan", dark: "#55DDE0", light: "#0E7490" },
    { id: "lavender", dark: "#A78BFA", light: "#6D28D9" },
    { id: "amber", dark: "#F5B942", light: "#B45309" },
    { id: "blue", dark: "#6E9BFF", light: "#2F5BEA" },
];

/** Matches the tokens in index.css: lime on dark, lavender on light. */
export const DEFAULT_ACCENT: AccentOption = { id: "default", dark: "#A3E635", light: "#6D28D9" };

/** Swatch that reads as selected: the explicit pick, or the option the default shows in this theme. */
export function selectedAccentId(option: AccentOption, theme: "light" | "dark"): AccentId {
    if (option.id !== "default") return option.id;
    return theme === "dark" ? "lime" : "lavender";
}

/** Five heatmap stops mixed in sRGB from `base` to `accent`; mixing in LCH swings the hue through unrelated colours. */
export function accentRamp(base: string, accent: string): string[] {
    const channels = (hex: string) => [1, 3, 5].map((index) => parseInt(hex.slice(index, index + 2), 16));
    const from = channels(base);
    const to = channels(accent);
    return [0, 0.3, 0.5, 0.75, 1].map(
        (weight) =>
            "#" +
            from
                .map((value, index) => Math.round(value + (to[index] - value) * weight).toString(16).padStart(2, "0"))
                .join(""),
    );
}

const STORAGE_KEY = "accent";
const STYLE_ID = "accent-override";
const listeners = new Set<() => void>();

function findOption(id: string | null): AccentOption {
    return ACCENT_OPTIONS.find((option) => option.id === id) ?? DEFAULT_ACCENT;
}

function readStoredId(): AccentOption["id"] {
    try {
        return findOption(localStorage.getItem(STORAGE_KEY)).id;
    } catch {
        return DEFAULT_ACCENT.id;
    }
}

let currentId: AccentOption["id"] = typeof window === "undefined" ? DEFAULT_ACCENT.id : readStoredId();

function injectStyle(option: AccentOption) {
    const existing = document.getElementById(STYLE_ID);
    if (option.id === DEFAULT_ACCENT.id) {
        existing?.remove();
        return;
    }
    const style = existing ?? document.createElement("style");
    style.id = STYLE_ID;
    // Doubled selectors outrank the token blocks in index.css regardless of order.
    style.textContent = `
:root:root {
    --color-accent: ${option.light};
    --color-accent-contrast: #FFFFFF;
    --color-accent-warm: ${option.light};
    --color-highlight-glow: ${option.light};
}
.dark.dark {
    --color-accent: ${option.dark};
    --color-accent-contrast: #0B0B0C;
    --color-accent-warm: ${option.dark};
    --color-highlight-glow: ${option.dark};
}`;
    if (!existing) document.head.appendChild(style);
}

/** Call once at startup so a stored pick applies before the first paint. */
export function applyStoredAccent() {
    injectStyle(findOption(currentId));
}

export function setAccent(id: AccentId) {
    const option = findOption(id);
    currentId = option.id;
    try {
        localStorage.setItem(STORAGE_KEY, option.id);
    } catch {
        // Storage blocked: the pick still applies for this page view.
    }
    injectStyle(option);
    listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

/** Active accent option; re-renders when the visitor picks another one. */
export function useAccentOption(): AccentOption {
    const id = useSyncExternalStore(subscribe, () => currentId, () => DEFAULT_ACCENT.id);
    return findOption(id);
}
